/*
 * Copyright (c) 2025 Hardfin, Inc.
 */
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { CreateTicketData, ticketSchema } from '@/ticket/api/types';
import { omit } from 'lodash-es';
import { z } from 'zod';

/**
 * The response schema for creating a ticket
 */
const createTicketResponseSchema = z.object({
  data: ticketSchema,
  metadata: responseMetadataSchema,
});

/**
 * The type for the response from the create ticket API
 */
type CreateTicketResponse = z.infer<typeof createTicketResponseSchema>;

/**
 * The parameters for creating a ticket
 */
interface CreateTicketParams {
  data: CreateTicketData;
}

/**
 * Creates a new ticket
 * @param params - The parameters for creating a ticket
 * @returns The created ticket
 */
export async function createTicket(
  params: CreateTicketParams
): Promise<CreateTicketResponse> {
  const path = `/v1/ticket`;
  const response = await api.post(path, {
    ...omit(params.data, 'assignedTo', 'assets', 'projects'),
    assignedTo: params.data.assignedTo?.id,
    assets: params.data.assets.map((asset) => asset.id),
    projects: params.data.projects.map((project) => project.id),
  });

  return parseResponse(createTicketResponseSchema, response.data, {
    schemaName: 'CreateTicket',
    endpoint: path,
  });
}
